import {
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Stack,
    Switch,
    TabPanel,
    TabPanels,
    Tabs,
    Text,
    useColorModeValue,
} from '@chakra-ui/react';
import CButton from 'components/CButton';
import CInput from 'components/CInput';
import GroupMembersDropdown from 'components/GroupMembersDropdown';
import TimeSlotEditor from 'components/TimeSlotEditor';
import { useMemberGroup } from 'contexts/MemberGroupContext';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export default function NewGroups() {
    const { createMemberGroup, getSingleMemberGroup, updateMemberGroup } = useMemberGroup();

    const navigate = useNavigate();
    const params = useParams();

    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const slots = [
        { key: 'mandatory_slot', label: 'Mandatory Slot', required: true },
        { key: 'optional_slot', label: 'Optional Slot', required: false },
    ];

    const [validated, setValidated] = useState(false);
    const [groupDetail, setGroupDetail] = useState({
        name: '',
        members: [],
        schedule: {
            mandatory_slot: { start_time: null, end_time: null, isRequired: true },
            optional_slot: { start_time: null, end_time: null, isRequired: false },
        },
        is_open_all_time: true
    });

    useEffect(() => {
        fetchSingleMemberGroup();

        return () => {
            setGroupDetail({
                name: '',
                members: [],
            })
        };
    }, [params.id]);

    const fetchSingleMemberGroup = async () => {
        if (params.id) {
            const singleGroupDetail = await getSingleMemberGroup(params.id);

            if (singleGroupDetail) {
                setGroupDetail(singleGroupDetail);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setValidated(true);

        if (!groupDetail.name) return;

        let hasError = false;
        let normalizedSchedule = groupDetail.schedule;

        if (!groupDetail.is_open_all_time) {
            normalizedSchedule = {};

            for (const { key, required } of slots) {
                const slot = groupDetail.schedule[key] || {};
                const start_time = slot.start_time?.trim?.() === '' ? null : slot.start_time;
                const end_time = slot.end_time?.trim?.() === '' ? null : slot.end_time;

                normalizedSchedule[key] = {
                    ...slot,
                    start_time,
                    end_time,
                };

                if (required && (!start_time || !end_time)) {
                    hasError = true;
                    break;
                }

                if (!required && ((start_time && !end_time) || (!start_time && end_time))) {
                    hasError = true;
                    break;
                }

                if (start_time && end_time && start_time >= end_time) {
                    hasError = true;
                    break;
                }
            }

            if (hasError) return;
        }

        const selectedMembers =
            groupDetail.members.length > 0
                ? groupDetail.members.map((member) => member.value)
                : [];

        try {
            const payload = {
                ...groupDetail,
                members: selectedMembers,
                schedule: groupDetail.is_open_all_time ? {} : normalizedSchedule,
                is_open_all_time: groupDetail.is_open_all_time
            };

            const data = params.id
                ? await updateMemberGroup(params.id, payload)
                : await createMemberGroup(payload);

            if (!data.error) {
                navigate('/admin/member/groups');
            }
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <Flex direction="column" minH="100vh" align="start" top="0" pt={{ sm: '125px', lg: '75px' }} position="relative" w="100%">
            <Tabs w="100%">
                <TabPanels mt="24px" maxW="100%" w="100%" mx="auto">
                    <TabPanel w="100%" p="0px" mx="auto">
                        <Text color={textColor} fontSize="2xl" fontWeight="700" mb="20px">
                            Member Group Info
                        </Text>
                        <form onSubmit={handleSubmit}>
                            <Flex direction="row" w="100%" justify="space-between">
                                <Stack direction="column" spacing="20px" w={{ base: "100%", md: "48%" }}>
                                    <CInput
                                        label="Group Name"
                                        type="text"
                                        value={groupDetail.name}
                                        onChange={(value) => setGroupDetail({ ...groupDetail, name: value })}
                                        required={true}
                                        validated={validated}
                                        placeholder="Enter group name"
                                        w="100%"
                                        mb="8px !important"
                                    />
                                    <GroupMembersDropdown
                                        value={groupDetail.members}
                                        onChange={(value) => setGroupDetail({ ...groupDetail, members: value })}
                                        multiSelect={true}
                                        group_id={params.id ? params.id : null}
                                        error={groupDetail.members.length === 0 && validated}
                                    />
                                </Stack>
                                <Box w={{ base: "100%", md: "48%" }} mr="8px">
                                    <FormControl display="flex" alignItems="center" mt="4">
                                        <FormLabel htmlFor="open-all-time" mb="0">
                                            Open All The Time
                                        </FormLabel>
                                        <Switch
                                            id="open-all-time"
                                            colorScheme="orange"
                                            isChecked={groupDetail.is_open_all_time}
                                            onChange={(e) => {
                                                const isOpenAllTime = e.target.checked;
                                                setValidated(false);
                                                setGroupDetail((prev) => ({
                                                    ...prev,
                                                    is_open_all_time: isOpenAllTime,
                                                    schedule: isOpenAllTime
                                                        ? Object.fromEntries(
                                                            slots.map(({ key, required }) => [
                                                                key,
                                                                { start_time: null, end_time: null, isRequired: required },
                                                            ])
                                                        )
                                                        : prev.schedule,
                                                }));
                                            }}
                                        />
                                    </FormControl>

                                    {!groupDetail.is_open_all_time && (
                                        <TimeSlotEditor
                                            schedule={groupDetail.schedule}
                                            onScheduleChange={(updatedSchedule) =>
                                                setGroupDetail((prevDetails) => ({
                                                    ...prevDetails,
                                                    schedule: updatedSchedule,
                                                }))
                                            }
                                            slots={slots}
                                            validated={validated}
                                        />
                                    )}
                                </Box>
                            </Flex>
                            <Flex justify="flex-end" align="center" mt="44px" gap="3">
                                <Button
                                    variant="darkBrand"
                                    fontSize="sm"
                                    borderRadius="10px"
                                    w={{ base: '128px', md: '148px' }}
                                    h="46px" type="button"
                                    bg="gray"
                                    color="white"
                                    _hover={{ bg: 'gray.600' }}
                                    onClick={() => navigate('/admin/member/groups')}>
                                    Cancel
                                </Button>
                                <CButton
                                    w={{ base: '128px', md: '148px' }}
                                    h="46px"
                                    type="submit">
                                    Submit
                                </CButton>
                            </Flex>
                        </form>
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </Flex>
    );
}
